import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { ArrowLeft, Lock, CheckCircle2, Trophy } from 'lucide-react';

// Badge definitions - Friendly progression ranks
const MILITARY_BADGES = [
  {
    id: 'explorer',
    name: 'Explorer',
    subtitle: 'The Curious',
    requirement: 'Complete daily challenges',
    image: 'https://static.prod-images.emergentagent.com/jobs/0e4a2968-ed3e-4331-a029-4f720e5976d0/images/917e49fff7fabb10e344c885fd0255d33876d5a59b41de299d0d8f3ae0479260.png',
    icon: '🧭',
    color: 'from-sky-400 to-blue-500',
    bgColor: 'bg-sky-50',
  },
  {
    id: 'pathfinder',
    name: 'Pathfinder',
    subtitle: 'The Earner',
    requirement: 'Earn Brain Coins',
    image: 'https://customer-assets.emergentagent.com/job_0e4a2968-ed3e-4331-a029-4f720e5976d0/artifacts/ueah2crq_Gemini_Generated_Image_di28mzdi28mzdi28.png',
    icon: '🗺️',
    color: 'from-emerald-400 to-green-500',
    bgColor: 'bg-emerald-50',
  },
  {
    id: 'trailblazer',
    name: 'Trailblazer',
    subtitle: 'The Consistent',
    requirement: 'Build a Brain Streak',
    image: 'https://customer-assets.emergentagent.com/job_0e4a2968-ed3e-4331-a029-4f720e5976d0/artifacts/z2hplm7s_Gemini_Generated_Image_16di4516di4516di.png',
    icon: '🔥',
    color: 'from-orange-400 to-red-500',
    bgColor: 'bg-orange-50',
  },
  {
    id: 'mastermind',
    name: 'Mastermind',
    subtitle: 'The Strategist',
    requirement: 'Adopt pets',
    image: 'https://customer-assets.emergentagent.com/job_0e4a2968-ed3e-4331-a029-4f720e5976d0/artifacts/qd57x4xh_Gemini_Generated_Image_k9e7c2k9e7c2k9e7.png',
    icon: '🧠',
    color: 'from-violet-400 to-purple-500',
    bgColor: 'bg-violet-50',
  },
  {
    id: 'brain_champion',
    name: 'Brain Champion',
    subtitle: 'The Ultimate Learner',
    requirement: 'Master all challenges',
    image: 'https://customer-assets.emergentagent.com/job_0e4a2968-ed3e-4331-a029-4f720e5976d0/artifacts/v5okopak_Gemini_Generated_Image_4gw6oc4gw6oc4gw6.png',
    icon: '👑',
    color: 'from-amber-400 to-yellow-500',
    bgColor: 'bg-amber-50',
  }
];

// Single Badge Card Component - Clean Icon on White Background
const BadgeCard = ({ badge, isUnlocked, index }) => {
  return (
    <div 
      className="relative group"
      data-testid={`badge-${badge.id}`}
    >
      {/* Badge Container - Pure White Background */}
      <div className={`
        relative overflow-hidden rounded-2xl
        aspect-square
        bg-white
        border-3 ${isUnlocked ? 'border-primary shadow-lg shadow-primary/20' : 'border-slate-200'}
        transition-all duration-300
        ${isUnlocked ? 'hover:scale-105 hover:shadow-xl' : ''}
      `}>
        {/* Badge Icon - Large Emoji on White */}
        <div className={`
          absolute inset-0 flex items-center justify-center
          ${!isUnlocked ? 'grayscale opacity-40' : ''}
        `}>
          <span className="text-6xl md:text-7xl">{badge.icon}</span>
        </div>
        
        {/* Locked Overlay */}
        {!isUnlocked && (
          <div className="absolute inset-0 flex items-center justify-center bg-white/70">
            <div className="w-12 h-12 rounded-full bg-slate-100 border-2 border-slate-300 flex items-center justify-center shadow-md">
              <Lock className="w-6 h-6 text-slate-400" />
            </div>
          </div>
        )}
        
        {/* Unlocked Checkmark */}
        {isUnlocked && (
          <div className="absolute top-2 right-2 w-8 h-8 bg-green-500 rounded-full flex items-center justify-center shadow-lg border-2 border-white">
            <CheckCircle2 className="w-5 h-5 text-white" strokeWidth={3} />
          </div>
        )}
        
        {/* Rank Number - Colorful */}
        <div className={`
          absolute top-2 left-2 w-8 h-8 rounded-full 
          flex items-center justify-center text-sm font-bold text-white
          shadow-md
          bg-gradient-to-br ${badge.color}
        `}>
          {index + 1}
        </div>
      </div>
    </div>
  );
};

const RewardsPage = () => {
  const { api } = useAuth();
  const navigate = useNavigate();
  const [rankData, setRankData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchRankData();
  }, []);

  const fetchRankData = async () => {
    try {
      const response = await api.get('/military-rank');
      setRankData(response.data);
    } catch (error) {
      console.error('Failed to fetch rank data:', error);
    } finally {
      setLoading(false);
    }
  };

  const isRankUnlocked = (badgeId) => {
    if (!rankData?.rank) return false;
    const currentRankOrder = MILITARY_BADGES.findIndex(b => b.id === rankData.rank.id);
    const badgeOrder = MILITARY_BADGES.findIndex(b => b.id === badgeId);
    return badgeOrder <= currentRankOrder;
  };

  const unlockedCount = MILITARY_BADGES.filter(b => isRankUnlocked(b.id)).length;
  const currentBadge = rankData?.rank ? MILITARY_BADGES.find(b => b.id === rankData.rank.id) : null;

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-purple-50 to-pink-50 flex items-center justify-center">
        <div className="text-center">
          <div className="text-6xl animate-bounce mb-4">🏆</div>
          <p className="text-xl text-slate-600 font-fredoka">Loading Badges...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-purple-50 to-pink-50" data-testid="badge-collection-page">
      {/* Header - Bright */}
      <header className="bg-white/80 backdrop-blur-sm border-b-2 border-slate-100 sticky top-0 z-50">
        <div className="max-w-4xl mx-auto px-4 py-3 flex items-center justify-between">
          <button
            onClick={() => navigate('/dashboard')}
            className="p-2 hover:bg-slate-100 rounded-full transition-colors"
            data-testid="back-btn"
          >
            <ArrowLeft className="w-5 h-5 text-slate-600" />
          </button>
          
          <h1 className="text-lg font-bold text-slate-900 font-fredoka flex items-center gap-2">
            <Trophy className="w-5 h-5 text-primary" />
            Badge Collection
          </h1>
          
          <div className="px-3 py-1 bg-gradient-to-r from-primary to-amber-400 rounded-full">
            <span className="text-black text-sm font-bold">{unlockedCount}/6</span>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-4xl mx-auto px-4 py-6 space-y-6">
        {/* Current Rank Banner - Colorful */}
        <div className="card-bubbly bg-gradient-to-r from-primary/10 to-amber-100/50">
          <div className="flex items-center gap-4">
            {/* Current Badge Preview - Icon on White */}
            <div className="w-20 h-20 md:w-24 md:h-24 rounded-2xl overflow-hidden border-3 border-primary shadow-lg flex-shrink-0 bg-white flex items-center justify-center">
              {currentBadge ? (
                <span className="text-5xl md:text-6xl">{currentBadge.icon}</span>
              ) : (
                <Lock className="w-8 h-8 text-slate-400" />
              )}
            </div>
            
            {/* Rank Info */}
            <div className="flex-1 min-w-0">
              <p className="text-slate-500 text-xs uppercase tracking-wider mb-1">Your Current Rank</p>
              <h2 className="text-2xl md:text-3xl font-black text-slate-900 truncate font-fredoka">
                {rankData?.rank?.name || 'Scout'}
              </h2>
              <p className="text-primary font-medium">
                {currentBadge?.subtitle || 'Begin your journey!'}
              </p>
            </div>
          </div>
        </div>

        {/* Badge Grid - 3x2 Layout with White Backgrounds */}
        <div className="grid grid-cols-3 gap-4 md:gap-6">
          {MILITARY_BADGES.map((badge, index) => (
            <BadgeCard
              key={badge.id}
              badge={badge}
              isUnlocked={isRankUnlocked(badge.id)}
              index={index}
            />
          ))}
        </div>

        {/* How to Earn - Bright Cards */}
        <div className="card-bubbly">
          <h3 className="text-lg font-bold text-slate-900 mb-4 text-center font-fredoka flex items-center justify-center gap-2">
            <Sparkles className="w-5 h-5 text-primary" />
            How to Earn Badges
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {MILITARY_BADGES.map((badge, index) => (
              <div 
                key={badge.id}
                className={`
                  flex items-center gap-3 p-3 rounded-xl
                  ${isRankUnlocked(badge.id) 
                    ? 'bg-green-50 border-2 border-green-200' 
                    : 'bg-slate-50 border-2 border-slate-100'
                  }
                `}
              >
                <span className={`
                  w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold text-white
                  bg-gradient-to-br ${badge.color}
                `}>
                  {index + 1}
                </span>
                <div className="flex-1 min-w-0">
                  <span className={`text-sm font-bold block truncate ${isRankUnlocked(badge.id) ? 'text-green-700' : 'text-slate-700'}`}>
                    {badge.name}
                  </span>
                  <span className={`text-xs ${isRankUnlocked(badge.id) ? 'text-green-600' : 'text-slate-500'}`}>
                    {badge.requirement}
                  </span>
                </div>
                {isRankUnlocked(badge.id) && (
                  <CheckCircle2 className="w-5 h-5 text-green-500 flex-shrink-0" />
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Motivation Message */}
        <div className="text-center py-4">
          <p className="text-slate-500 text-sm">
            {unlockedCount === 6 
              ? "🎉 Congratulations! You've earned all badges!" 
              : "🧠 Complete brain training sessions to unlock more badges!"
            }
          </p>
        </div>
      </main>
    </div>
  );
};

// Add Sparkles icon import
const Sparkles = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M12 3l1.912 5.813a2 2 0 001.275 1.275L21 12l-5.813 1.912a2 2 0 00-1.275 1.275L12 21l-1.912-5.813a2 2 0 00-1.275-1.275L3 12l5.813-1.912a2 2 0 001.275-1.275L12 3z"/>
  </svg>
);

export default RewardsPage;
